/**
 * CreateProductModal.tsx
 *
 * Unified Product, Variant, and Media Creation Workspace:
 * - 4-Tab workflow: Core Details, Sector Schema Extension, Bounded Variant Matrix, and Content-Addressed Media.
 * - Dynamic Sector Inspector (Physical, Fashion, Food, Electronics, Pharma, Services, Digital Goods).
 * - Pre-materialization combinatorial variant estimator (<= 100 limit guard).
 * - Multi-locale content and draft -> review -> active lifecycle management.
 * - Direct inventory ledger seeding for physical goods.
 */

import React, { useState } from 'react';
import { globalCatalogService } from '../../plugins/commerce/catalog-service.js';
import { globalInventoryMovementLedger } from '../../plugins/commerce/inventory/inventoryMovementLedger.js';
import {
  globalSchemaRegistry,
  estimateVariantCount,
  generateVariantCombinations,
  type GeneratedVariantDraft,
} from '../../plugins/commerce/schema-registry.js';
import { MediaDropzone } from './MediaDropzone.js';
import type {
  CommerceScope,
  SectorType,
  SchemaExtension,
  ProductOption,
  ProductStatus,
} from '../../plugins/commerce/commerce-contracts.js';
import type { MediaAsset } from '../../plugins/commerce/inventory/inventoryContracts.js';
import type { AuthorizationContext } from '../../kernel/authorization/capabilities.js';

export interface CreateProductModalProps {
  readonly isOpen: boolean;
  readonly authContext: AuthorizationContext;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

type TabType = 'core' | 'sector' | 'variants' | 'media';

export const CreateProductModal: React.FC<CreateProductModalProps> = ({
  isOpen,
  authContext,
  onClose,
  onCreated,
}) => {
  if (!isOpen) return null;

  const [activeTab, setActiveTab] = useState<TabType>('core');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // Tab 1: Core Details
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Apparel');
  const [basePriceFormatted, setBasePriceFormatted] = useState('29.99');
  const [currency, setCurrency] = useState('USD');
  const [baseSku, setBaseSku] = useState('PROD-001');
  const [status, setStatus] = useState<ProductStatus>('active');
  const [initialLocationId, setInitialLocationId] = useState('loc-main');
  const [initialStockQty, setInitialStockQty] = useState(25);

  // Tab 2: Sector Extension
  const [sector, setSector] = useState<SectorType>('fashion');

  // Fashion state
  const [fashionSize, setFashionSize] = useState('M');
  const [fashionColor, setFashionColor] = useState('Navy');
  const [fashionMaterial, setFashionMaterial] = useState('100% Organic Cotton');

  // Food state
  const [foodPerishability, setFoodPerishability] = useState<'high' | 'medium' | 'low'>('medium');
  const [foodAllergens, setFoodAllergens] = useState('dairy, gluten');

  // Electronics state
  const [elecSerialized, setElecSerialized] = useState(false);
  const [elecWarrantyMonths, setElecWarrantyMonths] = useState(12);

  // Pharma state
  const [pharmaRx, setPharmaRx] = useState(false);
  const [pharmaSubstances, setPharmaSubstances] = useState('Acetaminophen');

  // Service state
  const [srvDuration, setSrvDuration] = useState(60);
  const [srvDeliveryMode, setSrvDeliveryMode] = useState<'remote' | 'onsite' | 'appointment'>('remote');
  const [srvType, setSrvType] = useState<'consultation' | 'repair' | 'workshop' | 'class'>('consultation');
  const [srvLocationReq, setSrvLocationReq] = useState<'remote' | 'on-site' | 'in-store'>('remote');

  // Digital state
  const [digDelivery, setDigDelivery] = useState<'license_key' | 'file_download' | 'external_url'>('license_key');
  const [digLicense, setDigLicense] = useState<'perpetual' | 'subscription' | 'single-use'>('perpetual');
  const [digMaxActivations, setDigMaxActivations] = useState(3);

  // Tab 3: Variants
  const [options, setOptions] = useState<ProductOption[]>([
    {
      optionId: 'opt-size',
      name: { 'en-US': 'Size' },
      values: [
        { valueId: 'val-s', label: { 'en-US': 'S' } },
        { valueId: 'val-m', label: { 'en-US': 'M' } },
        { valueId: 'val-l', label: { 'en-US': 'L' } },
      ],
    },
    {
      optionId: 'opt-color',
      name: { 'en-US': 'Color' },
      values: [
        { valueId: 'val-navy', label: { 'en-US': 'Navy' } },
        { valueId: 'val-white', label: { 'en-US': 'White' } },
      ],
    },
  ]);
  const [generatedVariants, setGeneratedVariants] = useState<readonly GeneratedVariantDraft[]>([]);
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionValues, setNewOptionValues] = useState('');

  // Tab 4: Media
  const [attachedMedia, setAttachedMedia] = useState<readonly MediaAsset[]>([]);

  const estimate = estimateVariantCount(options);

  const handleGenerateVariants = () => {
    const res = generateVariantCombinations(options, baseSku.trim() || 'SKU');
    if (!res.success) {
      setErrorBanner(res.error || 'Failed to generate variants');
      return;
    }
    setGeneratedVariants(res.variants);
    setErrorBanner(null);
  };

  const handleAddOption = () => {
    if (!newOptionName.trim() || !newOptionValues.trim()) return;
    const vals = newOptionValues
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => ({
        valueId: `val-${v.toLowerCase().replace(/[^a-z0-9]/g, '')}`,
        label: { 'en-US': v },
      }));

    if (vals.length === 0) return;

    setOptions((prev) => [
      ...prev,
      {
        optionId: `opt-${Date.now()}`,
        name: { 'en-US': newOptionName.trim() },
        values: vals,
      },
    ]);
    setNewOptionName('');
    setNewOptionValues('');
  };

  const handleRemoveOption = (index: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const constructSchemaExtension = (): SchemaExtension | undefined => {
    switch (sector) {
      case 'fashion':
        return {
          sector: 'fashion',
          schemaVersion: '1.0.0',
          size: fashionSize,
          color: fashionColor,
          materialComposition: fashionMaterial,
        };
      case 'food':
        return {
          sector: 'food',
          schemaVersion: '1.0.0',
          perishability: foodPerishability,
          allergens: foodAllergens
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean),
        };
      case 'electronics':
        return {
          sector: 'electronics',
          schemaVersion: '1.0.0',
          serialized: elecSerialized,
          warrantyMonths: Number(elecWarrantyMonths),
        };
      case 'pharma':
        return {
          sector: 'pharma',
          schemaVersion: '1.0.0',
          prescriptionRequired: pharmaRx,
          activeSubstances: pharmaSubstances
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        };
      case 'service':
        return {
          sector: 'service',
          schemaVersion: '1.0.0',
          durationMinutes: Number(srvDuration),
          deliveryMode: srvDeliveryMode,
          serviceType: srvType,
          locationRequirement: srvLocationReq,
        };
      case 'digital':
        return {
          sector: 'digital',
          schemaVersion: '1.0.0',
          deliveryMethod: digDelivery,
          licenseType: digLicense,
          maxActivations: Number(digMaxActivations),
        };
      default:
        return undefined;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrorBanner('Product title is required.');
      setActiveTab('core');
      return;
    }

    setIsSubmitting(true);
    setErrorBanner(null);

    const priceCents = Math.round(parseFloat(basePriceFormatted || '0') * 100);
    const basePriceMinor = isNaN(priceCents) ? '0' : String(priceCents);

    const scope: CommerceScope = {
      meshId: 'mesh-alpha',
      workspaceId: 'ws-main',
      salesChannelId: 'channel-online',
      actorPrincipalId: authContext.principal.id,
      policyVersion: '1.0.0',
    };

    try {
      const ext = constructSchemaExtension();
      if (ext) {
        const valRes = globalSchemaRegistry.validateExtension(ext);
        if (!valRes.valid) {
          throw new Error(`Schema Extension Error: ${valRes.errors.join('; ')}`);
        }
      }

      // Convert attached media to AttachmentRef
      const mediaRefs = attachedMedia.map((m) => ({
        attachmentId: m.mediaId,
        contentHash: m.contentHash,
        mimeType: m.mimeType,
        sizeBytes: m.byteLength,
        altText: m.altText,
      }));

      // 1. Create Product in Catalog
      const createdProduct = await globalCatalogService.createProduct(scope, {
        title: { 'en-US': title.trim() },
        description: description.trim() ? { 'en-US': description.trim() } : undefined,
        status,
        categoryIds: [category],
        optionDefinitions: options,
        media: mediaRefs,
        schemaExtension: ext,
      });

      // 2. Create Variants
      if (generatedVariants.length > 0) {
        for (const gv of generatedVariants) {
          await globalCatalogService.createVariant(scope, {
            productId: createdProduct.productId,
            sku: gv.skuSuffix,
            attributes: gv.attributes,
            inventoryItemId: `inv_${gv.skuSuffix}`,
            basePriceMinor,
            currency,
            status: 'active',
          });

          // Register SKU product name in inventory movement ledger
          const prodTitle = typeof title === 'string' ? title : title['en-US'] || Object.values(title)[0] || 'Product';
          globalInventoryMovementLedger.setProductName(gv.skuSuffix, prodTitle);

          // Seed physical inventory if applicable
          if (sector !== 'service' && sector !== 'digital' && initialStockQty > 0) {
            globalInventoryMovementLedger.receiveStock(authContext, {
              sku: gv.skuSuffix,
              locationId: initialLocationId,
              lotNumber: `LOT-${gv.skuSuffix}-01`,
              quantity: initialStockQty,
            });
          }
        }
      } else {
        // Create 1 Default Master Variant
        const defaultSku = baseSku.trim() || `SKU-${Date.now().toString().slice(-4)}`;
        await globalCatalogService.createVariant(scope, {
          productId: createdProduct.productId,
          sku: defaultSku,
          attributes: { Standard: 'Default' },
          inventoryItemId: `inv_${defaultSku}`,
          basePriceMinor,
          currency,
          status: 'active',
        });

        const prodTitle = typeof title === 'string' ? title : title['en-US'] || Object.values(title)[0] || 'Product';
        globalInventoryMovementLedger.setProductName(defaultSku, prodTitle);

        if (sector !== 'service' && sector !== 'digital' && initialStockQty > 0) {
          globalInventoryMovementLedger.receiveStock(authContext, {
            sku: defaultSku,
            locationId: initialLocationId,
            lotNumber: `LOT-${defaultSku}-01`,
            quantity: initialStockQty,
          });
        }
      }

      onCreated();
      onClose();
    } catch (err: any) {
      setErrorBanner(err.message || 'Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--theme-bg-card, #111827)',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '780px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: 'var(--theme-text-primary, #f8fafc)' }}>
              📦 Create New Product & Inventory Record
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)' }}>
              Multi-sector catalog configuration, bounded variant generation, and content-addressed media ingestion.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--theme-text-secondary, #94a3b8)',
              fontSize: '20px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation Strip */}
        <div
          style={{
            display: 'flex',
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            padding: '0 20px',
          }}
        >
          {[
            { id: 'core', label: '1. Core Details' },
            { id: 'sector', label: '2. Sector & Type' },
            { id: 'variants', label: '3. Variant Matrix' },
            { id: 'media', label: '4. Media & DAM' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                padding: '12px 18px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 700,
                color: activeTab === tab.id ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-secondary, #94a3b8)',
                borderBottom: activeTab === tab.id ? '2px solid var(--theme-accent, #38bdf8)' : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error Banner */}
        {errorBanner && (
          <div
            style={{
              margin: '16px 24px 0',
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid #ef4444',
              color: '#fca5a5',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            ⚠️ {errorBanner}
          </div>
        )}

        {/* Modal Body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* TAB 1: CORE DETAILS */}
          {activeTab === 'core' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                    Product Title *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Organic Cotton Crewneck Sweatshirt"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--theme-border, #334155)',
                      backgroundColor: 'rgba(0, 0, 0, 0.25)',
                      color: '#fff',
                      fontSize: '13px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                    Base SKU / Prefix *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. SWTR-ORG"
                    value={baseSku}
                    onChange={(e) => setBaseSku(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--theme-border, #334155)',
                      backgroundColor: 'rgba(0, 0, 0, 0.25)',
                      color: '#fff',
                      fontSize: '13px',
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Provide comprehensive product specifications and overview..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--theme-border, #334155)',
                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    color: '#fff',
                    fontSize: '13px',
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--theme-border, #334155)',
                      backgroundColor: '#1e293b',
                      color: '#fff',
                      fontSize: '13px',
                    }}
                  >
                    <option value="AISurveillance">AI Surveillance &amp; Cameras</option>
                    <option value="BiometricAccess">Biometric Access &amp; Smart Locks</option>
                    <option value="DataSecurity">Data Security &amp; Hardware MFA</option>
                    <option value="RoboticsPatrol">Autonomous Robotics &amp; Patrol</option>
                    <option value="BallisticProtection">Ballistic Armor &amp; Shields</option>
                    <option value="ExecutiveProtection">Executive Protection &amp; Counter-Surveillance</option>
                    <option value="EliteFieldTech">Elite Tactical &amp; Night Vision Tech</option>
                    <option value="Electronics">Consumer Electronics</option>
                    <option value="Apparel">Apparel &amp; Gear</option>
                    <option value="Services">Professional Security Services</option>
                    <option value="DigitalGoods">Digital &amp; Software Licenses</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                    Base Price ({currency})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="29.99"
                    value={basePriceFormatted}
                    onChange={(e) => setBasePriceFormatted(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--theme-border, #334155)',
                      backgroundColor: 'rgba(0, 0, 0, 0.25)',
                      color: '#fff',
                      fontSize: '13px',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                    Publication Lifecycle Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ProductStatus)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--theme-border, #334155)',
                      backgroundColor: '#1e293b',
                      color: '#fff',
                      fontSize: '13px',
                    }}
                  >
                    <option value="active">Active (Available for Sale)</option>
                    <option value="draft">Draft (Work in Progress)</option>
                    <option value="review">Under Review</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              {/* Physical Inventory Seeding Block */}
              {sector !== 'service' && sector !== 'digital' && (
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(56, 189, 248, 0.05)',
                    border: '1px solid rgba(56, 189, 248, 0.2)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                  }}
                >
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#38bdf8' }}>
                      Initial Receiving Location
                    </label>
                    <select
                      value={initialLocationId}
                      onChange={(e) => setInitialLocationId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid var(--theme-border, #334155)',
                        backgroundColor: '#1e293b',
                        color: '#fff',
                        fontSize: '12px',
                      }}
                    >
                      <option value="loc-main">Main Fulfillment Center (loc-main)</option>
                      <option value="loc-counter">Front Retail Counter (loc-counter)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#38bdf8' }}>
                      Initial In-Stock Units per Variant
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={initialStockQty}
                      onChange={(e) => setInitialStockQty(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid var(--theme-border, #334155)',
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        color: '#fff',
                        fontSize: '12px',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: SECTOR & TYPE INSPECTOR */}
          {activeTab === 'sector' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                  Industry Sector Classification
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
                  {[
                    { id: 'fashion', label: '👗 Fashion' },
                    { id: 'food', label: '🥗 Food & CPG' },
                    { id: 'electronics', label: '⚡ Electronics' },
                    { id: 'pharma', label: '💊 Pharma' },
                    { id: 'service', label: '⏱️ Services' },
                    { id: 'digital', label: '💾 Digital Goods' },
                  ].map((sec) => (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => setSector(sec.id as SectorType)}
                      style={{
                        padding: '10px 8px',
                        borderRadius: '8px',
                        border: sector === sec.id ? '2px solid #38bdf8' : '1px solid var(--theme-border, #334155)',
                        backgroundColor: sector === sec.id ? 'rgba(56, 189, 248, 0.15)' : 'rgba(0, 0, 0, 0.2)',
                        color: sector === sec.id ? '#38bdf8' : 'var(--theme-text-secondary, #94a3b8)',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {sec.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dynamic Sector Inspector Box */}
              <div
                style={{
                  padding: '16px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--theme-border, #1e293b)',
                }}
              >
                {sector === 'fashion' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Size</label>
                      <input
                        type="text"
                        value={fashionSize}
                        onChange={(e) => setFashionSize(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Color</label>
                      <input
                        type="text"
                        value={fashionColor}
                        onChange={(e) => setFashionColor(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Material Composition</label>
                      <input
                        type="text"
                        value={fashionMaterial}
                        onChange={(e) => setFashionMaterial(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                  </div>
                )}

                {sector === 'food' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Perishability Level</label>
                      <select
                        value={foodPerishability}
                        onChange={(e) => setFoodPerishability(e.target.value as any)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      >
                        <option value="high">High (Perishable Daily/Weekly)</option>
                        <option value="medium">Medium (Months)</option>
                        <option value="low">Low (Shelf-Stable)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Allergens (Comma-separated)</label>
                      <input
                        type="text"
                        value={foodAllergens}
                        onChange={(e) => setFoodAllergens(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                  </div>
                )}

                {sector === 'electronics' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Warranty (Months)</label>
                      <input
                        type="number"
                        value={elecWarrantyMonths}
                        onChange={(e) => setElecWarrantyMonths(Number(e.target.value))}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '20px' }}>
                      <input
                        type="checkbox"
                        checked={elecSerialized}
                        onChange={(e) => setElecSerialized(e.target.checked)}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>Track Serial Numbers (Individual Units)</span>
                    </div>
                  </div>
                )}

                {sector === 'service' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Duration (Minutes)</label>
                      <input
                        type="number"
                        value={srvDuration}
                        onChange={(e) => setSrvDuration(Number(e.target.value))}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Service Type</label>
                      <select
                        value={srvType}
                        onChange={(e) => setSrvType(e.target.value as any)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      >
                        <option value="consultation">Consultation</option>
                        <option value="repair">Maintenance / Repair</option>
                        <option value="workshop">Interactive Workshop</option>
                        <option value="class">Group Class</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Delivery Mode</label>
                      <select
                        value={srvDeliveryMode}
                        onChange={(e) => setSrvDeliveryMode(e.target.value as any)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      >
                        <option value="remote">Remote (Video / Mesh Call)</option>
                        <option value="onsite">On-Site (Client Location)</option>
                        <option value="appointment">In-Store Appointment</option>
                      </select>
                    </div>
                  </div>
                )}

                {sector === 'digital' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Delivery Method</label>
                      <select
                        value={digDelivery}
                        onChange={(e) => setDigDelivery(e.target.value as any)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      >
                        <option value="license_key">License Key Provisioning</option>
                        <option value="file_download">Direct File Download</option>
                        <option value="external_url">External Portal Token</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>License Model</label>
                      <select
                        value={digLicense}
                        onChange={(e) => setDigLicense(e.target.value as any)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      >
                        <option value="perpetual">Perpetual License</option>
                        <option value="subscription">Recurring Subscription</option>
                        <option value="single-use">Single-Use Activation</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Max Activations</label>
                      <input
                        type="number"
                        value={digMaxActivations}
                        onChange={(e) => setDigMaxActivations(Number(e.target.value))}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: VARIANT MATRIX */}
          {activeTab === 'variants' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--theme-text-primary, #f8fafc)' }}>
                  Option Axes & Values
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: estimate.status === 'ok' ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: estimate.status === 'ok' ? '#34d399' : '#f87171',
                  }}
                >
                  Estimated Variants: {estimate.count} / 100 max
                </span>
              </div>

              {/* Existing Options List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {options.map((opt, idx) => (
                  <div
                    key={opt.optionId}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid var(--theme-border, #1e293b)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '12px' }}>{Object.values(opt.name)[0]}: </span>
                      <span style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)' }}>
                        {opt.values.map((v) => Object.values(v.label)[0]).join(', ')}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(idx)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#f87171',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New Option Axis */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr auto',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  border: '1px dashed var(--theme-border, #334155)',
                }}
              >
                <input
                  type="text"
                  placeholder="Axis (e.g. Material)"
                  value={newOptionName}
                  onChange={(e) => setNewOptionName(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155', fontSize: '12px' }}
                />
                <input
                  type="text"
                  placeholder="Values (comma-separated, e.g. Cotton, Linen, Wool)"
                  value={newOptionValues}
                  onChange={(e) => setNewOptionValues(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', backgroundColor: '#1e293b', color: '#fff', border: '1px solid #334155', fontSize: '12px' }}
                />
                <button
                  type="button"
                  onClick={handleAddOption}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--theme-accent, #38bdf8)',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '12px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  + Add Axis
                </button>
              </div>

              {/* Generate Matrix Button */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={handleGenerateVariants}
                  disabled={estimate.status !== 'ok'}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    backgroundColor: estimate.status === 'ok' ? '#3b82f6' : '#475569',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '12px',
                    border: 'none',
                    cursor: estimate.status === 'ok' ? 'pointer' : 'not-allowed',
                  }}
                >
                  ⚡ Generate Combinatorial Variants Matrix
                </button>
                {generatedVariants.length > 0 && (
                  <span style={{ fontSize: '12px', color: '#34d399', fontWeight: 600 }}>
                    ✓ {generatedVariants.length} Variants Generated
                  </span>
                )}
              </div>

              {/* Generated Variants Preview */}
              {generatedVariants.length > 0 && (
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #1e293b', backgroundColor: 'rgba(0,0,0,0.3)', color: '#94a3b8', fontSize: '11px' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>SKU</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Combination</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedVariants.map((gv) => (
                        <tr key={gv.combinationKey} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 700 }}>
                            <code>{gv.skuSuffix}</code>
                          </td>
                          <td style={{ padding: '8px 12px', color: '#cbd5e1' }}>
                            {Object.entries(gv.attributes).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: MEDIA & DAM */}
          {activeTab === 'media' && (
            <div>
              <MediaDropzone
                ownerType="product"
                ownerId={baseSku || 'prod-new'}
                existingMedia={attachedMedia}
                authContext={authContext}
                onMediaAttached={(m) => setAttachedMedia((prev) => [...prev, m])}
                onMediaRemoved={(id) => setAttachedMedia((prev) => prev.filter((m) => m.mediaId !== id))}
                onSetPrimary={(id) => {
                  setAttachedMedia((prev) =>
                    prev.map((m) => ({
                      ...m,
                      role: m.mediaId === id ? 'primary' : 'gallery',
                    }))
                  );
                }}
              />
            </div>
          )}

          {/* Modal Footer Controls */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid var(--theme-border, #1e293b)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: '1px solid var(--theme-border, #334155)',
                color: 'var(--theme-text-secondary, #94a3b8)',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--theme-accent, #38bdf8)',
                  color: '#000',
                  fontWeight: 800,
                  fontSize: '13px',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(56, 189, 248, 0.3)',
                }}
              >
                {isSubmitting ? 'Saving...' : '💾 Save & Publish Product'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
